import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { apiService, Device, PendingInvite, FavoriteUrl } from '../services/api';
import { database } from '../firebase';
import { ref, onValue, get } from 'firebase/database';
import { toBlockedUrlEntry } from '../utils/blockedUrl';
import { normalizeUrl } from '../utils/url';

interface DeviceContextType {
  devices: Device[];
  selectedDevice: Device | null;
  setSelectedDevice: (device: Device | null) => void;
  deviceActions: any[];
  pendingInvites: PendingInvite[];
  loading: boolean;
  snackbar: { open: boolean; message: string; severity: 'success' | 'error' };
  showSnackbar: (message: string, severity?: 'success' | 'error') => void;
  closeSnackbar: () => void;
  loadDevices: () => Promise<void>;
  loadPendingInvites: () => Promise<void>;
  reloadActions: () => Promise<void>;
  handleVolumeChange: (deviceId: string, newValue: number) => Promise<void>;
  handleAddBlockedUrl: (url: string) => Promise<void>;
  /** True when the write landed. Shows no snackbar: the confirm dialog reports the result (BA-55). */
  handleRemoveBlockedUrl: (deviceId: string, url: string) => Promise<boolean>;
  handleBanCurrentUrl: (deviceId: string, banType?: 'page' | 'domain') => Promise<void>;
  handleAddFavoriteUrl: (url: string) => Promise<void>;
  /** True when the write landed. Shows no snackbar: the confirm dialog reports the result (BA-55). */
  handleRemoveFavoriteUrl: (deviceId: string, url: string) => Promise<boolean>;
  /** Undo of a removal: puts the favourite back at min(index, length) unless it is already there. */
  handleRestoreFavoriteUrl: (deviceId: string, favorite: FavoriteUrl, index: number) => Promise<boolean>;
  /** Re-reads one URL list from the backend into the device; null when the read fails. */
  reloadUrlList: (deviceId: string, kind: 'blocked' | 'favorite') => Promise<string[] | null>;
  handleFavoriteCurrentUrl: (deviceId: string) => Promise<void>;
  handleMoveFavoriteUp: (deviceId: string, url: string) => Promise<void>;
  handleMoveFavoriteDown: (deviceId: string, url: string) => Promise<void>;
  handleSendToBike: (deviceId: string, minutes: number) => Promise<void>;
  handleSendToSleep: (deviceId: string, minutes: number) => Promise<void>;
  handleInviteParent: (email: string, role: 'viewer' | 'editor') => Promise<void>;
  handleAddDevice: (name: string) => Promise<void>;
  handleAcceptInvite: (token: string) => Promise<void>;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

/**
 * The favourites with a removed one put back at min(index, length), renumbered; null when an
 * entry with the same stored url is already there (Undo then writes nothing).
 */
export function withFavoriteRestored(
  favorites: FavoriteUrl[], favorite: FavoriteUrl, index: number,
): FavoriteUrl[] | null {
  if (favorites.some(f => f.url === favorite.url)) return null;
  const ordered = [...favorites].sort((a, b) => a.order - b.order);
  ordered.splice(Math.min(index, ordered.length), 0, favorite);
  return ordered.map((f, i) => ({ ...f, order: i }));
}

/** A 409 from the backend (an ApiError): the list changed on every compare-and-set retry. */
const isListChanged = (error: unknown) => (error as { status?: number } | null)?.status === 409;

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }
  return context;
};

export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deviceActions, setDeviceActions] = useState<any[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getDevices();
      setDevices(response.devices);
      if (!selectedDevice && response.devices.length > 0) {
        setSelectedDevice(response.devices[0]);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
      showSnackbar(t('device.failedToLoadDevices'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice, showSnackbar, t]);

  const loadPendingInvites = useCallback(async () => {
    try {
      const response = await apiService.getPendingInvites();
      setPendingInvites(response.pending_invites);
    } catch (error) {
      console.error('Failed to load pending invites:', error);
    }
  }, []);

  const reloadActions = useCallback(async () => {
    if (!selectedDevice) return;

    try {
      const actionsRef = ref(database, `devices/${selectedDevice.id}/actions`);
      const snapshot = await get(actionsRef);
      const data = snapshot.val();

      if (!data) {
        setDeviceActions([]);
        return;
      }

      const actionsArray = Object.entries(data).map(([id, actionData]: [string, any]) => ({
        id,
        name: actionData.name,
        description: actionData.description,
        pictureUrl: actionData.pictureUrl || null,
        thumbnailUrl: actionData.thumbnailUrl || null,
        soundUrl: actionData.soundUrl || null,
        defaults: actionData.defaults || null,
        createdAt: actionData.createdAt,
        updatedAt: actionData.updatedAt,
        updatedBy: actionData.updatedBy,
      }));

      setDeviceActions(actionsArray);
    } catch (error) {
      console.error('Failed to reload actions:', error);
    }
  }, [selectedDevice]);

  // Load devices and invites on user change
  useEffect(() => {
    if (!currentUser) return;
    loadDevices();
    loadPendingInvites();
  }, [currentUser, loadDevices, loadPendingInvites]);

  // Subscribe to current_url changes in Firebase
  useEffect(() => {
    if (!selectedDevice) return;

    const deviceStatusRef = ref(database, `devices/${selectedDevice.id}/status/current_url`);
    const unsubscribe = onValue(deviceStatusRef, (snapshot) => {
      const currentUrl = snapshot.val();
      if (currentUrl !== undefined) {
        setSelectedDevice(prev => prev ? { ...prev, current_url: currentUrl } : null);
        setDevices(prev => prev.map(d =>
          d.id === selectedDevice.id ? { ...d, current_url: currentUrl } : d
        ));
      }
    });

    return () => unsubscribe();
  }, [selectedDevice?.id]);

  // Subscribe to actions for selected device
  useEffect(() => {
    if (!selectedDevice) {
      setDeviceActions([]);
      return;
    }

    const actionsRef = ref(database, `devices/${selectedDevice.id}/actions`);
    const unsubscribe = onValue(actionsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setDeviceActions([]);
        return;
      }

      const actionsArray = Object.entries(data).map(([id, actionData]: [string, any]) => ({
        id,
        name: actionData.name,
        description: actionData.description,
        pictureUrl: actionData.pictureUrl || null,
        thumbnailUrl: actionData.thumbnailUrl || null,
        soundUrl: actionData.soundUrl || null,
        defaults: actionData.defaults || null,
        createdAt: actionData.createdAt,
        updatedAt: actionData.updatedAt,
        updatedBy: actionData.updatedBy,
      }));

      setDeviceActions(actionsArray);
    });

    return () => unsubscribe();
  }, [selectedDevice?.id]);

  const handleVolumeChange = useCallback(async (deviceId: string, newValue: number) => {
    try {
      await apiService.updateDeviceConfig(deviceId, { volume: newValue });
      setDevices(prev => prev.map(d => d.id === deviceId ? { ...d, volume: newValue } : d));
      if (selectedDevice?.id === deviceId) {
        setSelectedDevice(prev => prev ? { ...prev, volume: newValue } : null);
      }
      showSnackbar(t('device.volumeUpdated'));
    } catch (error) {
      console.error('Failed to update volume:', error);
      showSnackbar(t('device.failedToUpdateVolume'), 'error');
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  const handleAddBlockedUrl = useCallback(async (url: string) => {
    if (!selectedDevice || !url) return;
    try {
      const entry = toBlockedUrlEntry(url);
      if (!entry) return;
      const updatedUrls = [...(selectedDevice.blocked_urls || []), entry];
      await apiService.updateBlockedUrls(selectedDevice.id, updatedUrls);
      setDevices(prev => prev.map(d => d.id === selectedDevice.id ? { ...d, blocked_urls: updatedUrls } : d));
      setSelectedDevice(prev => prev ? { ...prev, blocked_urls: updatedUrls } : null);
      showSnackbar(t('urls.urlBlockedSuccess'));
    } catch (error) {
      console.error('Failed to block URL:', error);
      showSnackbar(t('urls.failedToBlockUrl'), 'error');
    }
  }, [selectedDevice, showSnackbar, t]);

  const setBlockedUrls = useCallback((deviceId: string, blockedUrls: string[]) => {
    setDevices(prev => prev.map(d => d.id === deviceId ? { ...d, blocked_urls: blockedUrls } : d));
    setSelectedDevice(prev => prev && prev.id === deviceId ? { ...prev, blocked_urls: blockedUrls } : prev);
  }, []);

  const setFavoriteUrls = useCallback((deviceId: string, favoriteUrls: FavoriteUrl[]) => {
    setDevices(prev => prev.map(d => d.id === deviceId ? { ...d, favorite_urls: favoriteUrls } : d));
    setSelectedDevice(prev => prev && prev.id === deviceId ? { ...prev, favorite_urls: favoriteUrls } : prev);
  }, []);

  const handleRemoveBlockedUrl = useCallback(async (deviceId: string, url: string) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      if (!device) return false;
      const updatedUrls = device.blocked_urls.filter(u => u !== url);
      await apiService.updateBlockedUrls(deviceId, updatedUrls);
      setBlockedUrls(deviceId, updatedUrls);
      return true;
    } catch (error) {
      console.error('Failed to unblock URL:', error);
      return false;
    }
  }, [devices, setBlockedUrls]);

  const handleBanCurrentUrl = useCallback(async (deviceId: string, banType: 'page' | 'domain' = 'page') => {
    try {
      const device = devices.find(d => d.id === deviceId);
      if (!device || !device.current_url) {
        await apiService.updateDevice(deviceId, { current_url: 'https://example.com' });
        showSnackbar(t('urls.testUrlSet'));
        await loadDevices();
        return;
      }

      // The backend appends the current URL (or its host, for the domain) to the stored list in
      // a compare-and-set (BA-94), never a list built from this copy, so a ban that landed
      // meanwhile is kept (BA-103)
      const updatedDevice = await apiService.updateDevice(deviceId, {
        ban_current_url: true,
        ...(banType === 'domain' ? { ban_current_domain: true } : {}),
      });
      setDevices(prev => prev.map(d => d.id === deviceId ? updatedDevice : d));
      if (selectedDevice?.id === deviceId) {
        setSelectedDevice(updatedDevice);
      }

      showSnackbar(t(banType === 'domain' ? 'urls.domainBanned' : 'urls.currentUrlBanned'));
    } catch (error) {
      console.error('Failed to ban current URL:', error);
      showSnackbar(t(isListChanged(error) ? 'urls.listChanged' : 'urls.failedToBanCurrentUrl'), 'error');
    }
  }, [devices, selectedDevice?.id, loadDevices, showSnackbar, t]);

  const handleAddFavoriteUrl = useCallback(async (url: string) => {
    if (!selectedDevice || !url) return;
    try {
      const normalizedUrl = normalizeUrl(url);
      const currentFavorites = selectedDevice.favorite_urls || [];
      const maxOrder = currentFavorites.length > 0
        ? Math.max(...currentFavorites.map(f => f.order))
        : -1;
      const newFavorite: FavoriteUrl = { url: normalizedUrl, order: maxOrder + 1 };
      const updatedFavorites = [...currentFavorites, newFavorite];

      await apiService.updateFavoriteUrls(selectedDevice.id, updatedFavorites);

      setDevices(prev => prev.map(d =>
        d.id === selectedDevice.id ? { ...d, favorite_urls: updatedFavorites } : d
      ));
      setSelectedDevice(prev => prev ? { ...prev, favorite_urls: updatedFavorites } : null);

      showSnackbar(t('urls.urlAddedToFavorites'));
    } catch (error) {
      console.error('Failed to add favorite URL:', error);
      showSnackbar(t('urls.failedToAddFavorite'), 'error');
    }
  }, [selectedDevice, showSnackbar, t]);

  const handleRemoveFavoriteUrl = useCallback(async (deviceId: string, url: string) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      if (!device) return false;

      const updatedFavorites = device.favorite_urls
        .filter(f => f.url !== url)
        .map((f, index) => ({ ...f, order: index }));

      await apiService.updateFavoriteUrls(deviceId, updatedFavorites);
      setFavoriteUrls(deviceId, updatedFavorites);
      return true;
    } catch (error) {
      console.error('Failed to remove favorite URL:', error);
      return false;
    }
  }, [devices, setFavoriteUrls]);

  const handleRestoreFavoriteUrl = useCallback(async (deviceId: string, favorite: FavoriteUrl, index: number) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      if (!device) return false;
      const updatedFavorites = withFavoriteRestored(device.favorite_urls || [], favorite, index);
      if (!updatedFavorites) return true;

      await apiService.updateFavoriteUrls(deviceId, updatedFavorites);
      setFavoriteUrls(deviceId, updatedFavorites);
      return true;
    } catch (error) {
      console.error('Failed to restore favorite URL:', error);
      return false;
    }
  }, [devices, setFavoriteUrls]);

  const reloadUrlList = useCallback(async (deviceId: string, kind: 'blocked' | 'favorite') => {
    try {
      if (kind === 'blocked') {
        const { blocked_urls } = await apiService.getBlockedUrls(deviceId);
        const urls = blocked_urls || [];
        setBlockedUrls(deviceId, urls);
        return urls;
      }
      const { favorite_urls } = await apiService.getFavoriteUrls(deviceId);
      const favorites = favorite_urls || [];
      setFavoriteUrls(deviceId, favorites);
      return favorites.map(f => f.url);
    } catch (error) {
      console.error('Failed to reload URL list:', error);
      return null;
    }
  }, [setBlockedUrls, setFavoriteUrls]);

  const handleFavoriteCurrentUrl = useCallback(async (deviceId: string) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      if (!device || !device.current_url) {
        await apiService.updateDevice(deviceId, { current_url: 'https://example.com' });
        showSnackbar(t('urls.testUrlSetFavorite'));
        await loadDevices();
        return;
      }

      const updatedDevice = await apiService.updateDevice(deviceId, { favorite_current_url: true });
      setDevices(prev => prev.map(d => d.id === deviceId ? updatedDevice : d));
      if (selectedDevice?.id === deviceId) {
        setSelectedDevice(updatedDevice);
      }
      showSnackbar(t('urls.currentUrlAddedToFavorites'));
    } catch (error) {
      console.error('Failed to favorite current URL:', error);
      showSnackbar(t(isListChanged(error) ? 'urls.listChanged' : 'urls.failedToFavoriteCurrentUrl'), 'error');
    }
  }, [devices, selectedDevice?.id, loadDevices, showSnackbar, t]);

  const handleMoveFavoriteUp = useCallback(async (deviceId: string, url: string) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      if (!device) return;

      const favorites = [...device.favorite_urls].sort((a, b) => a.order - b.order);
      const index = favorites.findIndex(f => f.url === url);

      if (index <= 0) return;

      [favorites[index - 1], favorites[index]] = [favorites[index], favorites[index - 1]];
      const updatedFavorites = favorites.map((f, i) => ({ ...f, order: i }));

      await apiService.updateFavoriteUrls(deviceId, updatedFavorites);

      setDevices(prev => prev.map(d =>
        d.id === deviceId ? { ...d, favorite_urls: updatedFavorites } : d
      ));
      if (selectedDevice?.id === deviceId) {
        setSelectedDevice(prev => prev ? { ...prev, favorite_urls: updatedFavorites } : null);
      }
    } catch (error) {
      console.error('Failed to reorder favorite:', error);
      showSnackbar(t('urls.failedToReorderFavorite'), 'error');
    }
  }, [devices, selectedDevice?.id, showSnackbar, t]);

  const handleMoveFavoriteDown = useCallback(async (deviceId: string, url: string) => {
    try {
      const device = devices.find(d => d.id === deviceId);
      if (!device) return;

      const favorites = [...device.favorite_urls].sort((a, b) => a.order - b.order);
      const index = favorites.findIndex(f => f.url === url);

      if (index < 0 || index >= favorites.length - 1) return;

      [favorites[index], favorites[index + 1]] = [favorites[index + 1], favorites[index]];
      const updatedFavorites = favorites.map((f, i) => ({ ...f, order: i }));

      await apiService.updateFavoriteUrls(deviceId, updatedFavorites);

      setDevices(prev => prev.map(d =>
        d.id === deviceId ? { ...d, favorite_urls: updatedFavorites } : d
      ));
      if (selectedDevice?.id === deviceId) {
        setSelectedDevice(prev => prev ? { ...prev, favorite_urls: updatedFavorites } : null);
      }
    } catch (error) {
      console.error('Failed to reorder favorite:', error);
      showSnackbar(t('urls.failedToReorderFavorite'), 'error');
    }
  }, [devices, selectedDevice?.id, showSnackbar, t]);

  const handleSendToBike = useCallback(async (deviceId: string, minutes: number) => {
    try {
      const bikeTime = Date.now() + minutes * 60 * 1000;
      await apiService.updateDevice(deviceId, { status: 'bike', bike_time: bikeTime });
      setDevices(prev => prev.map(d => d.id === deviceId ? { ...d, status: 'bike', bike_time: bikeTime } : d));
      if (selectedDevice?.id === deviceId) {
        setSelectedDevice(prev => prev ? { ...prev, status: 'bike', bike_time: bikeTime } : null);
      }
      showSnackbar(t('bike.successMessage', { minutes }));
    } catch (error) {
      console.error('Failed to send to bike mode:', error);
      showSnackbar(t('bike.failed'), 'error');
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  const handleSendToSleep = useCallback(async (deviceId: string, minutes: number) => {
    try {
      const sleepTime = Date.now() + minutes * 60 * 1000;
      await apiService.updateDevice(deviceId, { status: 'sleep', sleep_time: sleepTime });
      setDevices(prev => prev.map(d => d.id === deviceId ? { ...d, status: 'sleep', sleep_time: sleepTime } : d));
      if (selectedDevice?.id === deviceId) {
        setSelectedDevice(prev => prev ? { ...prev, status: 'sleep', sleep_time: sleepTime } : null);
      }
      showSnackbar(t('sleep.successMessage', { minutes }));
    } catch (error) {
      console.error('Failed to send to sleep mode:', error);
      showSnackbar(t('sleep.failed'), 'error');
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  const handleInviteParent = useCallback(async (email: string, role: 'viewer' | 'editor') => {
    if (!selectedDevice || !email) return;

    try {
      const response = await apiService.inviteToDevice(selectedDevice.id, email, role);
      showSnackbar(t('invites.inviteSentTo', { email }));
    } catch (error) {
      console.error('Error sending invitation:', error);
      showSnackbar(t('invites.failedToSendInvitation'), 'error');
    }
  }, [selectedDevice, showSnackbar, t]);

  const handleAddDevice = useCallback(async (name: string) => {
    if (!name) return;
    try {
      const newDevice = await apiService.createDevice(name);
      setDevices(prev => [...prev, newDevice]);
      if (!selectedDevice) {
        setSelectedDevice(newDevice);
      }
      showSnackbar(t('device.deviceAdded'));
    } catch (error) {
      console.error('Failed to add device:', error);
      showSnackbar(t('device.failedToAddDevice'), 'error');
    }
  }, [selectedDevice, showSnackbar, t]);

  const handleAcceptInvite = useCallback(async (token: string) => {
    try {
      await apiService.acceptInvite(token);
      showSnackbar(t('invites.invitationAccepted'));
      loadDevices();
      loadPendingInvites();
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      showSnackbar(t('invites.failedToAcceptInvitation'), 'error');
    }
  }, [loadDevices, loadPendingInvites, showSnackbar, t]);

  const value: DeviceContextType = {
    devices,
    selectedDevice,
    setSelectedDevice,
    deviceActions,
    pendingInvites,
    loading,
    snackbar,
    showSnackbar,
    closeSnackbar,
    loadDevices,
    loadPendingInvites,
    reloadActions,
    handleVolumeChange,
    handleAddBlockedUrl,
    handleRemoveBlockedUrl,
    handleBanCurrentUrl,
    handleAddFavoriteUrl,
    handleRemoveFavoriteUrl,
    handleRestoreFavoriteUrl,
    reloadUrlList,
    handleFavoriteCurrentUrl,
    handleMoveFavoriteUp,
    handleMoveFavoriteDown,
    handleSendToBike,
    handleSendToSleep,
    handleInviteParent,
    handleAddDevice,
    handleAcceptInvite,
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};
